(() => {
  if (customElements.get('mys-halloween-auto-gift')) return;
  const items = cart => Array.isArray(cart?.items) ? cart.items : [];
  const quantity = item => Math.max(0, Math.trunc(Number(item.quantity) || 0));
  const hasBundle = item => Object.prototype.hasOwnProperty.call(item.properties || {}, '_MYS Bundle');
  const delay = duration => new Promise(resolve => setTimeout(resolve, duration));
  const freeUnits = item => {
    const original = Number(item.original_line_price);
    const allocated = (item.line_level_discount_allocations || []).reduce((sum, allocation) => sum + Math.max(0, Number(allocation.amount) || 0), 0);
    if (!original || !quantity(item) || !allocated) return 0;
    return Math.min(quantity(item), Math.floor((Math.min(allocated, original - Number(item.final_line_price)) * quantity(item) + 0.001) / original));
  };

  class HalloweenAutoGift extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-mys-auto-gift-config]').textContent);
      this.eligible = new Set(this.config.eligibleProducts);
      this.excluded = new Set(this.config.excludedProducts);
      this.queue = Promise.resolve();
      this.pending = new Set();
      this.version = 0;
      this.state = { state: 'idle', quantity: 0, message: '' };
      this.failureKey = 'mys-halloween-auto-gift-failed-2026';
      try { this.failedSignature = sessionStorage.getItem(this.failureKey) || ''; } catch (_) {}
      window.MysHalloweenAutoGift = this;
      this.events = new AbortController();
      const on = (target, name, listener, options = {}) => target.addEventListener(name, listener, { ...options, signal: this.events.signal });
      on(document, 'click', event => this.onClick(event), { capture: true });
      on(document, 'submit', event => this.onSubmit(event), { capture: true });
      on(window, 'pageshow', () => this.reconcile());
      on(window, 'focus', () => this.reconcile());
      on(document, 'visibilitychange', () => { if (!document.hidden) this.reconcile(); });
      this.unsubscribe = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' ? subscribe(PUB_SUB_EVENTS.cartUpdate, event => event?.source === 'mys-halloween-auto-gift' ? Promise.resolve() : this.reconcile()) : null;
      this.observer = new MutationObserver(() => this.renderState());
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.wasActive = this.active();
      this.clock = setInterval(() => {
        if (this.wasActive !== this.active()) { this.wasActive = this.active(); this.reconcile(); }
      }, 30000);
      this.reconcile();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.unsubscribe?.();
      this.observer?.disconnect();
      clearInterval(this.clock);
      if (window.MysHalloweenAutoGift === this) delete window.MysHalloweenAutoGift;
      this.initialized = false;
    }

    active() {
      if (this.config.designMode) return false;
      const now = Date.now();
      const start = Date.parse(this.config.start);
      const end = Date.parse(this.config.end);
      const testUntil = Date.parse(this.config.testUntil || '');
      const live = this.config.enabled === true && now >= start && now < end;
      const testing = Number.isFinite(testUntil) && testUntil <= start && now < start && now < testUntil;
      return live || testing;
    }

    needsGuard() { return !this.config.designMode && (this.active() || this.config.hasOwned === true || items(this.cart).some(item => this.owned(item))); }

    owned(item) {
      return String(item.variant_id) === this.config.giftVariant && item.properties?.['_MYS Halloween Auto Gift'] === '2026' && Boolean(item.properties?.['_MYS Halloween Auto Claim']);
    }

    mat(item) { return String(item.variant_id) === this.config.giftVariant && !hasBundle(item) && !item.selling_plan_allocation; }

    chair(item) { return this.eligible.has(String(item.product_id)) && !this.excluded.has(String(item.product_id)) && !hasBundle(item) && !item.selling_plan_allocation; }

    counts(cart) {
      const chairs = items(cart).reduce((sum, item) => sum + (this.chair(item) ? quantity(item) : 0), 0);
      const ordinary = items(cart).reduce((sum, item) => sum + (this.mat(item) && !this.owned(item) ? quantity(item) : 0), 0);
      const owned = items(cart).reduce((sum, item) => sum + (this.owned(item) ? quantity(item) : 0), 0);
      const free = items(cart).reduce((sum, item) => sum + (this.mat(item) ? freeUnits(item) : 0), 0);
      return { chairs, ordinary, owned, free, target: this.active() ? Math.max(0, chairs - ordinary) : 0 };
    }

    signature(cart) {
      return items(cart).filter(item => this.chair(item) || (this.mat(item) && !this.owned(item))).map(item => `${item.product_id}:${item.variant_id}:${quantity(item)}`).sort().join('|');
    }

    text(key, count = 0) { return (this.config.strings[key] || '').replace('__COUNT__', String(count)); }

    setState(state, count = 0, key = state) {
      this.state = { state, quantity: count, message: state === 'idle' ? '' : this.text(key, count) };
      this.renderState();
      document.dispatchEvent(new CustomEvent('mys:halloween-gift-state', { detail: this.state }));
    }

    renderState() {
      document.querySelectorAll('[data-mys-auto-gift-status]').forEach(node => {
        const preview = node.hasAttribute('data-mys-auto-gift-preview');
        if (preview) return;
        node.hidden = this.state.state === 'idle';
        node.dataset.mysAutoGiftState = this.state.state;
        node.setAttribute('aria-busy', String(this.state.state === 'working'));
        const message = node.querySelector('[data-mys-auto-gift-message]');
        if (message && message.textContent !== this.state.message) message.textContent = this.state.message;
        node.querySelectorAll('[data-mys-auto-gift-retry], [data-mys-auto-gift-continue]').forEach(button => { button.hidden = this.state.state !== 'error'; });
      });
    }

    beginMutation() {
      if (!this.needsGuard()) return () => {};
      const token = {};
      this.pending.add(token);
      this.version++;
      let ended = false;
      return () => { if (!ended) { ended = true; this.pending.delete(token); this.version++; } };
    }

    async waitForMutations() {
      const start = Date.now();
      while (this.pending.size) {
        if (Date.now() - start > 20000) throw new Error('Cart update still pending');
        await delay(30);
      }
    }

    async request(url, body) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
        if (!response.ok) throw new Error('Cart request failed');
        const result = await response.json();
        if (Number(result.status) >= 400) throw new Error('Cart update failed');
        return result;
      } finally { clearTimeout(timeout); }
    }

    async getCart() {
      const cart = await this.request(`${this.config.cartUrl}.js`);
      if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
      this.cart = cart;
      this.config.hasOwned = items(cart).some(item => this.owned(item));
      return cart;
    }

    rememberFailure(cart) {
      this.failedSignature = this.signature(cart);
      try { sessionStorage.setItem(this.failureKey, this.failedSignature); } catch (_) {}
    }

    clearFailure() {
      this.failedSignature = '';
      try { sessionStorage.removeItem(this.failureKey); } catch (_) {}
    }

    async trimOwned(all = false) {
      let cart = await this.getCart();
      for (let attempt = 0; attempt < 30; attempt++) {
        await this.waitForMutations();
        cart = await this.getCart();
        const counts = this.counts(cart);
        const excess = counts.owned - (all ? 0 : counts.target);
        if (excess <= 0) return cart;
        const item = items(cart).filter(line => this.owned(line)).at(-1);
        if (!item) return cart;
        await this.request(`${this.config.changeUrl}.js`, { id: item.key, quantity: Math.max(0, quantity(item) - excess) });
        cart = await this.getCart();
      }
      throw new Error('Gift adjustment unconfirmed');
    }

    async refreshTheme() {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mys-halloween-auto-gift', cartData: this.cart });
      const sections = ['cart-icon-bubble'];
      if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
      if (document.getElementById('main-cart-footer')) sections.push(document.getElementById('main-cart-footer').dataset.id || 'main-cart-footer');
      const rendered = await this.request(`${this.config.cartUrl}?sections=${sections.map(encodeURIComponent).join(',')}`);
      for (const section of sections) {
        if (!rendered[section]) continue;
        const parsed = new DOMParser().parseFromString(rendered[section], 'text/html');
        if (section === 'cart-drawer') {
          for (const selector of ['cart-drawer-items', '.cart-drawer__footer']) {
            const source = parsed.querySelector(selector);
            const target = document.querySelector(selector);
            if (target && source) target.replaceWith(source);
          }
          continue;
        }
        const target = section === 'cart-icon-bubble' ? document.getElementById(section) : document.getElementById('main-cart-footer');
        const source = section === 'cart-icon-bubble' ? parsed.querySelector('.shopify-section') : parsed.getElementById('main-cart-footer');
        if (target && source) target.innerHTML = source.innerHTML;
      }
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', items(this.cart).length === 0);
      this.renderState();
    }

    reconcile(options = {}) {
      if (this.config.designMode) return Promise.resolve({ ok: true, state: 'idle', cart: this.cart });
      if (!this.needsGuard()) { this.setState('idle'); return Promise.resolve({ ok: true, state: 'idle', cart: this.cart, version: this.version, active: this.active() }); }
      this.queue = (this.queue || Promise.resolve()).catch(() => {}).then(async () => {
        const work = () => this.runReconcile(options);
        return navigator.locks?.request ? navigator.locks.request('mys-halloween-auto-gift-2026', work) : work();
      });
      return this.queue;
    }

    async runReconcile(options) {
      let changed = false;
      try {
        if (options.retry) this.clearFailure();
        for (let attempt = 0; attempt < 8; attempt++) {
          await this.waitForMutations();
          const version = this.version;
          let cart = await this.getCart();
          if (this.pending.size || version !== this.version) continue;
          let counts = this.counts(cart);
          if (!this.active() || options.withoutGift) {
            if (counts.owned) {
              this.setState('working');
              cart = await this.trimOwned(true);
              changed = true;
            }
            this.setState('idle');
            if (changed) await this.refreshTheme();
            if (this.pending.size || version !== this.version) continue;
            if (!options.withoutGift && this.active()) continue;
            if (options.withoutGift) this.rememberFailure(cart);
            return { ok: this.counts(cart).owned === 0, state: 'idle', cart, version, active: this.active() };
          }
          this.setState('working');
          if (counts.owned > counts.target) { cart = await this.trimOwned(); changed = true; continue; }
          if (!counts.chairs) {
            this.setState('idle');
            if (changed) await this.refreshTheme();
            if (this.pending.size || version !== this.version) continue;
            return { ok: true, state: 'idle', cart, version, active: this.active() };
          }
          const remaining = counts.target - counts.owned;
          if (remaining > 0) {
            if (this.failedSignature && this.failedSignature === this.signature(cart)) throw new Error('Gift verification previously failed');
            if (!this.config.available || !navigator.locks?.request || !this.active()) throw new Error('Gift unavailable');
            const token = crypto.randomUUID();
            changed = true;
            this.config.hasOwned = true;
            await this.request(`${this.config.addUrl}.js`, { items: [{ id: this.config.giftVariant, quantity: remaining, properties: { '_MYS Halloween Auto Gift': '2026', '_MYS Halloween Auto Claim': token } }] });
            cart = await this.getCart();
            await this.waitForMutations();
            cart = await this.trimOwned();
          }
          if (this.pending.size || version !== this.version || !this.active()) continue;
          counts = this.counts(cart);
          if (!counts.chairs) continue;
          const ownFree = items(cart).filter(item => this.owned(item)).every(item => freeUnits(item) === quantity(item));
          if (!ownFree || counts.free < counts.chairs || counts.owned !== counts.target) throw new Error('Free gift not confirmed');
          this.clearFailure();
          this.setState('confirmed', counts.chairs);
          await this.refreshTheme();
          if (this.pending.size || version !== this.version || !this.active()) continue;
          return { ok: true, state: 'confirmed', cart, version, active: true };
        }
        throw new Error('Cart changed during gift verification');
      } catch (_) {
        try {
          await this.waitForMutations();
          const before = await this.getCart();
          const cart = await this.trimOwned(true);
          changed = changed || this.counts(before).owned > 0;
          this.rememberFailure(cart);
          this.setState('error', 0, 'error');
          if (changed) await this.refreshTheme();
          return { ok: false, state: 'error', cart };
        } catch (_) {
          this.setState('error', 0, 'uncertain');
          return { ok: false, state: 'error', cart: this.cart };
        }
      }
    }

    async saveCartFields(form) {
      const body = {};
      if (form) {
        const data = new FormData(form);
        for (const [name, value] of data) {
          if (name === 'note') body.note = String(value);
          const attribute = /^attributes\[(.+)\]$/.exec(name);
          if (attribute) { body.attributes ||= {}; body.attributes[attribute[1]] = String(value); }
        }
      }
      const note = document.querySelector('cart-note textarea');
      if (note) body.note = note.value;
      if (Object.keys(body).length) await this.request(`${this.config.updateUrl}.js`, body);
    }

    async prepareCheckout(form, withoutGift = false) {
      if (this.config.designMode) return false;
      try {
        await this.waitForMutations();
        await this.saveCartFields(form);
        for (let attempt = 0; attempt < 8; attempt++) {
          const result = await this.reconcile({ withoutGift });
          if (!result.ok) return false;
          if (this.pending.size || result.version !== this.version || result.active !== this.active()) continue;
          this.preparedCheckout = { version: result.version, active: result.active };
          return true;
        }
        throw new Error('Cart changed before checkout');
      } catch (_) { this.setState('error', 0, 'uncertain'); return false; }
    }

    async checkout(form, withoutGift = false) {
      if (this.checkoutBusy || this.config.designMode) return;
      this.checkoutBusy = true;
      try {
        for (let attempt = 0; attempt < 8; attempt++) {
          const ok = await this.prepareCheckout(form, withoutGift);
          if (!ok) return;
          if (this.pending.size || this.preparedCheckout?.version !== this.version || this.preparedCheckout?.active !== this.active()) continue;
          window.location.assign(this.config.checkoutUrl);
          return;
        }
        this.setState('error', 0, 'uncertain');
      } finally { this.checkoutBusy = false; }
    }

    onSubmit(event) {
      if (!this.needsGuard()) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const checkout = event.submitter?.name === 'checkout' || form.querySelector('input[name="checkout"]') || /\/checkout\/?$/.test(new URL(form.action, window.location.href).pathname);
      if (!checkout) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkout(form);
    }

    onClick(event) {
      const retry = event.target.closest?.('[data-mys-auto-gift-retry]');
      const proceed = event.target.closest?.('[data-mys-auto-gift-continue]');
      if (retry || proceed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (retry) this.reconcile({ retry: true });
        else this.checkout(document.getElementById('cart'), true);
        return;
      }
      const link = event.target.closest?.('a[href]');
      if (!this.needsGuard()) return;
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkout(document.getElementById('cart'));
    }
  }

  customElements.define('mys-halloween-auto-gift', HalloweenAutoGift);
})();
